import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getNotificationsList,
  type InputType as ListInputType,
} from "../endpoints/notifications/list_GET.schema";
import { getUnreadCount } from "../endpoints/notifications/unread-count_GET.schema";
import { useCallback } from "react";
import { postMarkRead } from "../endpoints/notifications/mark-read_POST.schema";
import { postMarkAllRead } from "../endpoints/notifications/mark-all-read_POST.schema";
import { postDeleteNotification } from "../endpoints/notifications/delete_POST.schema";
import { postGenerateNotifications } from "../endpoints/notifications/generate_POST.schema";
import { useEffect } from "react";

export const NOTIFICATIONS_KEYS = {
  all: ["notifications"] as const,
  listAll: ["notifications", "list"] as const,
  list: (params: ListInputType) =>
    [...NOTIFICATIONS_KEYS.all, "list", params] as const,
  unreadCount: ["notifications", "unreadCount"] as const,
};

export function useNotificationsList(params: Partial<ListInputType> = {}) {
  const fullParams: ListInputType = {
    limit: params.limit ?? 20,
    offset: params.offset ?? 0,
    unreadOnly: params.unreadOnly,
  };
  return useQuery({
    queryKey: NOTIFICATIONS_KEYS.list(fullParams),
    queryFn: () => getNotificationsList(fullParams),
    staleTime: 30000, // 30 seconds
    // Always refetch when the Notifications page/panel mounts so a notification
    // that arrived via push (or while the app was backgrounded) shows up
    // immediately. Required because the global QueryClient sets
    // refetchOnMount: false, which otherwise suppresses the mount refetch
    // regardless of staleTime. Scoped to this hook only.
    refetchOnMount: "always",
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: NOTIFICATIONS_KEYS.unreadCount,
    queryFn: () => getUnreadCount(),
    refetchInterval: 60000, // Refetch every 60 seconds (was 30s)
    staleTime: 30000, // Consider data fresh for 30 seconds
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number) => postMarkRead({ notificationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });
    },
    onError: (error) => {
      toast.error(`Failed to mark as read: ${error.message}`);
    },
  });
}

export function useMarkAllRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => postMarkAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });
      toast.success("All notifications marked as read");
    },
    onError: (error) => {
      toast.error(`Failed to mark all as read: ${error.message}`);
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: number) =>
      postDeleteNotification({ notificationId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });
      toast.success("Notification deleted");
    },
    onError: (error) => {
      toast.error(`Failed to delete notification: ${error.message}`);
    },
  });
}

export function useGenerateNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => postGenerateNotifications(),
    onSuccess: (data) => {
      if (data.generated > 0) {
        queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.all });
      }
    },
  });
}

// Hook to automatically generate notifications on mount (e.g. in dashboard)
export function useAutoGenerateNotifications() {
  const { mutate } = useGenerateNotifications();
  
  useEffect(() => {
    mutate();
  }, [mutate]);
}

export function useRefreshNotificationsList() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.listAll });
  }, [queryClient]);
}